/**
 * WorkflowEditor — the routine editor/preview pane (right pane of the split view, P12 §6 / T-FE3).
 *
 * The editor is the shared draft made visible: it fills in live from RoutineBuilder's `routine_draft`
 * patches, and is human-editable once the agent is idle (v2 §0 — read-only only while the agent owns
 * the draft mid-turn). This component:
 *   - reads the shared draft from {@link useRoutineDraftContext} (one store per route, shared with the
 *     chat pane) — a prop change just renders, so agent patches fill the steps live;
 *   - renders the n8n-style node graph (Schedule → Query → optional Condition → Slack) with an
 *     Add-condition affordance when no gate exists yet;
 *   - the name/description + action bar (Run / Discard / Save) now live in the page-level
 *     {@link RoutineHeader}, not here;
 *   - flashes a brief highlight ring on a step the moment it's freshly filled by a patch (§6.5).
 *
 * Activation lives on the Schedule Trigger node; run history is its own page. Storybook injects
 * `initialDraft`/`initialTestRun` via the provider + a story-only test-run.
 */
import { useEffect, useRef, useState } from 'react';

import { Callout } from '@/_shared/components/ui/callout';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { useWorkspaceOrNull } from '@/_shared/contexts/WorkspaceContext';

import { useRoutineDraftContext } from '../contexts/RoutineDraftContext';
import { useChannelOptions } from '../hooks/useChannelOptions';
import { useWarehouseOptions } from '../hooks/useWarehouseOptions';
import { RESUME_ADMIN_ONLY, workflowRepairState } from '../lib/repair';
import { type RoutineField, validateRoutineDraft } from '../lib/validation';
import { warehousePickPatch } from '../mappers';
import type { StepKey, TestRunResult } from '../types';
import { ConditionStep } from './ConditionStep';
import { Connector } from './Connector';
import { QueryStep } from './QueryStep';
import { ScheduleTrigger } from './ScheduleTrigger';
import { SlackStep } from './SlackStep';

interface WorkflowEditorProps {
  /**
   * Override dry-run result — used by the Storybook story for the preview state.
   * The live editor always uses the actions-layer dry run.
   */
  initialTestRun?: TestRunResult | null;
  /** When true (Storybook), suppresses live dry-run wiring and uses `initialTestRun`. */
  storyMode?: boolean;
  /**
   * Whether the RoutineBuilder agent is mid-turn. The steps are READ-ONLY while the agent works (it
   * owns the draft during its turn); the human may edit only once the agent is idle (v2 §0). Defaults
   * to editable (false) for Storybook/standalone renders.
   */
  agentActive?: boolean;
}

/**
 * Per-step highlight: flash the ring briefly when the step's agent nonce changes — a fresh agent
 * patch filled it (§6.5). `prev` seeds from the initial nonce, so the first render never flashes.
 */
function useStepHighlight(nonce: number): boolean {
  const [highlighted, setHighlighted] = useState(false);
  const prev = useRef(nonce);
  useEffect(() => {
    if (nonce === prev.current) return;
    prev.current = nonce;
    setHighlighted(true);
    const t = setTimeout(() => setHighlighted(false), 1200);
    return () => clearTimeout(t);
  }, [nonce]);
  return highlighted;
}

export function WorkflowEditor({
  initialTestRun,
  storyMode = false,
  agentActive = false,
}: WorkflowEditorProps): React.ReactElement {
  const { store, actions } = useRoutineDraftContext();
  const { draft, staleSteps, isLoading, workflowStatus } = store;
  // Storybook renders the editor without a workspace; nobody is an admin there.
  const isAdmin = useWorkspaceOrNull()?.isAdmin ?? false;

  const warehouseOpts = useWarehouseOptions();
  const channelOpts = useChannelOptions();

  // Dry run lives on the actions layer; Storybook injects a static result instead.
  const dryRun = actions.dryRun;
  const testRun = storyMode ? (initialTestRun ?? null) : dryRun.result;
  const isRunning = storyMode ? false : dryRun.isRunning;

  // v2 §0: the human edits the steps only when the agent is idle (the agent owns the draft mid-turn).
  const isReadOnly = !!agentActive;

  // Form validation: a field's error shows only once it's been touched (blurred) or a Save/Preview was
  // attempted — and never while the agent is mid-fill. Selects (warehouse/channel) can't be manually
  // emptied, so they surface only on submit; the text fields also surface on blur.
  const fieldErrors = validateRoutineDraft(draft);
  const visibleError = (field: RoutineField): string | undefined =>
    !isReadOnly && (store.touchedFields.has(field) || store.submitAttempted) ? fieldErrors[field] : undefined;

  // Highlight rings — flash a step ONLY when the agent patches it (keyed on the per-step agent nonce,
  // not the draft content), so the ring never fires on the human's own edits (e.g. toggling a switch).
  const queryHighlight = useStepHighlight(store.agentNonces.query);
  const conditionHighlight = useStepHighlight(store.agentNonces.condition);
  const slackHighlight = useStepHighlight(store.agentNonces.slack);

  function statusFor(step: StepKey) {
    if (isRunning) return 'loading' as const;
    if (!testRun) return 'idle' as const;
    return staleSteps.has(step) ? ('stale' as const) : ('fresh' as const);
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-6 overflow-hidden px-6 py-8">
        <Skeleton className="h-12 w-64" />
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-36 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  const isActive = workflowStatus === 'active';
  const isSaved = !!store.workflowId;
  // ACT2-1286: while repair-paused the switch is Resume — admin only, and only once every step is repaired.
  const repair = workflowRepairState(store.accountRepair);
  const resumeBlockedReason = !repair.repairPaused
    ? null
    : !isAdmin
      ? RESUME_ADMIN_ONLY
      : repair.broken
        ? "Repair this workflow's warehouse account first."
        : null;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* ── Dry-run error banner ── */}
      {dryRun.error && !storyMode && (
        <Callout variant="error" role="alert" className="rounded-none border-b border-border whitespace-pre-line">
          Dry run failed: {dryRun.error}
        </Callout>
      )}

      {/* ── Canvas ── n8n-style dotted node-graph backdrop; the step cards float on top. */}
      <div
        className="min-h-0 flex-1 overflow-auto px-6 py-8"
        style={{
          backgroundImage: 'radial-gradient(circle, var(--grey-75) 1px, transparent 1.5px)',
          backgroundSize: '1.25rem 1.25rem',
        }}
      >
        <div className="mx-auto flex w-full max-w-[37.5rem] flex-col">
          {/* Node 0 · Schedule Trigger — owns when-to-run (cron) + the Active toggle (start/pause). */}
          <ScheduleTrigger
            schedule={draft.schedule}
            onScheduleChange={(schedule) => store.applyUserEdit({ schedule })}
            isActive={isActive}
            isSaved={isSaved}
            onToggleActive={(active) => (active ? actions.start() : actions.pause())}
            isToggling={actions.isStarting || actions.isPausing}
            canToggle={isSaved}
            readOnly={isReadOnly}
            repairPaused={repair.repairPaused}
            resumeLabel={isAdmin}
            resumeBlockedReason={resumeBlockedReason}
          />

          <QueryStep
            warehouseId={draft.warehouseId}
            sql={draft.sql}
            status={statusFor('query')}
            result={testRun ? testRun.query : null}
            error={dryRun.stepErrors.query}
            onWarehouseChange={(warehouseId) => store.applyUserEdit(warehousePickPatch(warehouseId), 'query')}
            onSqlChange={(sql) => store.applyUserEdit({ sql }, 'query')}
            warehouseError={visibleError('warehouseId')}
            sqlError={visibleError('sql')}
            onSqlBlur={() => store.markTouched('sql')}
            warehouseOptions={warehouseOpts.options}
            warehouseOptionsLoading={warehouseOpts.isLoading}
            readOnly={isReadOnly}
            highlight={queryHighlight}
          />
          <Connector />

          {/* Condition / send-policy step — always shown. Its header switch toggles it: off = "Send
              always" (no condition), on = "Send only if…" (condition fields). */}
          <ConditionStep
            stepNumber={2}
            condition={draft.condition}
            status={statusFor('condition')}
            result={testRun ? testRun.condition : null}
            error={dryRun.stepErrors.condition}
            onChange={(condition) => store.applyUserEdit({ condition }, 'condition')}
            valueError={visibleError('condition')}
            onValueBlur={() => store.markTouched('condition')}
            readOnly={isReadOnly}
            highlight={conditionHighlight}
          />
          <Connector />

          <SlackStep
            stepNumber={3}
            channel={draft.channel}
            template={draft.template}
            status={statusFor('slack')}
            result={testRun ? testRun.slack : null}
            error={dryRun.stepErrors.slack}
            onChannelChange={(channel) => store.applyUserEdit({ channel }, 'slack')}
            onTemplateChange={(template) => store.applyUserEdit({ template }, 'slack')}
            channelError={visibleError('channel')}
            templateError={visibleError('template')}
            onTemplateBlur={() => store.markTouched('template')}
            channelOptions={channelOpts.options}
            channelOptionsLoading={channelOpts.isLoading}
            channelAccessDenied={channelOpts.isAccessDenied}
            columns={dryRun.columns}
            readOnly={isReadOnly}
            highlight={slackHighlight}
          />
        </div>
      </div>
    </div>
  );
}
